import React, { useState, useEffect } from "react";
import type { PageType } from "../utils/albumData";
import { Popover, Typography, Button, Slider, IconButton, Box, Tooltip } from "@mui/material";
import { CuratedBackgroundDialog } from "../components/CuratedBackgroundDialog";

interface CuratedBackground {
    small: string;
    original: string;
}


interface BackgroundControlsProps {
    pageId: string;
    showBackground: boolean;
    setShowBackground: (value: boolean) => void;
    backgroundOpacity: number;
    setBackgroundOpacity: (value: number) => void;
    backgroundZoom: number;
    setBackgroundZoom: (value: number) => void;
    backgroundImageSrc: string | null;
    backgroundOffsetX: number;
    setBackgroundOffsetX: (value: number) => void; // page-specific
    backgroundOffsetY: number;
    setBackgroundOffsetY: (value: number) => void; // page-specific
    backgroundImageOriginalSize: { width: number; height: number } | null;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    updatePageById: (pageId: string, changes: Partial<PageType>) => void;
    onRemoveBackground: (pageId: string) => void;
    stageWidth: number;
    stageHeight: number;
    setPageBackgroundImage: (pageId: string, src: string | null) => void;
}

const BackgroundControls: React.FC<BackgroundControlsProps> = ({
    pageId,
    backgroundOpacity,
    setBackgroundOpacity,
    backgroundZoom,
    setBackgroundZoom,
    backgroundImageSrc,
    backgroundOffsetX,
    setBackgroundOffsetX,
    backgroundOffsetY,
    setBackgroundOffsetY,
    backgroundImageOriginalSize,
    fileInputRef,
    stageWidth,
    stageHeight,
    onRemoveBackground,  
    setPageBackgroundImage,

    
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showCurated, setShowCurated] = useState(false);
    const [curatedBackgrounds, setCuratedBackgrounds] = useState<CuratedBackground[]>([]);
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const open = Boolean(anchorEl);

    // Load JSON
    useEffect(() => {
        fetch(`${process.env.PUBLIC_URL}/eda_backgrounds/backgrounds.json`)
            .then(res => res.json())
            .then((data: CuratedBackground[]) => setCuratedBackgrounds(data))
            .catch(err => console.error("Failed to load curated backgrounds:", err));
    }, []);

    // Calculate zoomed image size
    // Zoomed image dimensions
    const imgWidth = 1500; // your fixed small image width
    const imgHeight = imgWidth / (backgroundImageOriginalSize?.width ?? 1) * (backgroundImageOriginalSize?.height ?? 1);

    const zoomedWidth = imgWidth * backgroundZoom;
    const zoomedHeight = imgHeight * backgroundZoom;

    // Maximum allowed offsets so image cannot show blank space
    const maxXOffset = Math.max(0, (zoomedWidth - stageWidth) / 2);
    const maxYOffset = Math.max(0, (zoomedHeight - stageHeight) / 2);

   
    return (
        <>
            <Tooltip title="Background Settings" arrow>
                <IconButton
                    onClick={handleOpen}
                    sx={{
                        p: 0,
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        color: "#59b3d8",
                        transition: "border-color 0.2s, box-shadow 0.2s, transform 0.15s",
                        "&:hover": {
                            borderColor: "#59b3d8",
                            border: "2px solid #59b3d8",
                            backgroundColor: "rgba(25, 118, 210, 0.1)",
                        },
                        "&:active": { borderColor: "#59b3d8" },
                    }}
                >
                    <img
                        src={`${process.env.PUBLIC_URL}/background.png`}
                        alt="Background"
                        style={{ width: 26, height: 24, display: "block", pointerEvents: "none" }}
                    />
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ p: 2, width: 180, display: "flex", flexDirection: "column", gap: 0.1 }}>
                    <Typography sx={{ fontSize: 15, mb: 1 }}>Background Settings</Typography>

                    {/* Opacity */}
                    <div>
                        <Typography sx={{ fontSize: 13 }}>Opacity</Typography>
                        <Slider
                            value={backgroundOpacity}
                            onChange={(e, newValue) => setBackgroundOpacity(newValue as number)}
                            min={0}
                            max={1}
                            step={0.01}
                            sx={{
                                height: 6,
                                width: "100%",
                                "& .MuiSlider-thumb": { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                "& .MuiSlider-rail": { height: 4, color: "#59b3d8" },
                                "& .MuiSlider-track": { height: 4, color: "#59b3d8" },
                            }}
                        />
                    </div>

                    {/* Zoom */}
                    <div>
                        <Typography sx={{ fontSize: 14 }}>Zoom</Typography>
                        <Slider
                            value={backgroundZoom}
                            onChange={(e, newValue) => setBackgroundZoom(newValue as number)}
                            min={1}
                            max={2}
                            step={0.01}
                            sx={{
                                height: 6,
                                width: "100%",
                                "& .MuiSlider-thumb": { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                "& .MuiSlider-rail": { height: 4, color: "#59b3d8" },
                                "& .MuiSlider-track": { height: 4, color: "#59b3d8" },
                            }}
                        />
                    </div>

                    {/* Move X */}
                    <div>
                        <Typography sx={{ fontSize: 14 }}>Move X</Typography>
                        <Slider
                            value={backgroundOffsetX}
                            onChange={(e, newValue) => setBackgroundOffsetX(newValue as number)}
                            min={-maxXOffset}
                            max={maxXOffset}
                            step={1}
                            sx={{
                                height: 6,
                                width: "100%",
                                "& .MuiSlider-thumb": { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                "& .MuiSlider-rail": { height: 4, color: "#59b3d8" },
                                "& .MuiSlider-track": { height: 4, color: "#59b3d8" },
                            }}
                        />
                    </div>

                    {/* Move Y */}
                    <div>
                        <Typography sx={{ fontSize: 14 }}>Move Y</Typography>
                        <Slider
                            value={backgroundOffsetY}
                            onChange={(e, newValue) => setBackgroundOffsetY(newValue as number)}
                            min={-maxYOffset}
                            max={maxYOffset}
                            step={1}
                            sx={{
                                height: 6,
                                width: "100%",
                                "& .MuiSlider-thumb": { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                "& .MuiSlider-rail": { height: 4, color: "#59b3d8" },
                                "& .MuiSlider-track": { height: 4, color: "#59b3d8" },
                            }}
                        />
                    </div>

                    {/* Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                        }}
                    >
                        {/* Top row */}
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ fontSize: 12, py: 0.5 }}
                            >
                                ADD IMAGE
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setShowCurated(true)}
                                disableRipple
                                sx={{ fontSize: 12, py: 0.5 }}
                            >
                                SELECT IMAGE
                            </Button>
                        </Box>

                        {/* Bottom row */}
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => onRemoveBackground(pageId)}  //  calls parent logic
                            disabled={!backgroundImageSrc}
                            sx={{ fontSize: 12, padding: "2px 12px" }}
                        >
                            REMOVE IMAGE
                        </Button>
                    </Box>
                    {/* Curated Backgrounds Dialog */}
                    <CuratedBackgroundDialog
                        open={showCurated}
                        onClose={() => setShowCurated(false)}
                        curatedBackgrounds={curatedBackgrounds}
                        selectedBackground={backgroundImageSrc}
                        pageId={pageId}
                        setPageBackgroundImage={setPageBackgroundImage}
                    />
                </Box>
            </Popover>
        </>
    );
};

export default BackgroundControls;
